import { ArrowRight, CheckCircle2, Circle, KeyRound, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Container } from '../components/ui/Container';
import { PageHero } from '../components/ui/PageHero';
import { changePassword, signOut, useAuth } from '../lib/auth';
import { formatUnitLabel, ownerUnits } from '../lib/pb';

const MIN_LENGTH = 6;
const DEFAULT_PASSWORD = 'Welcome3873';

const PASSWORD_RULES: { label: string; test: (pw: string) => boolean }[] = [
  { label: `At least ${MIN_LENGTH} characters`, test: (pw) => pw.length >= MIN_LENGTH },
  { label: 'One capital letter', test: (pw) => /[A-Z]/.test(pw) },
  { label: 'One number', test: (pw) => /[0-9]/.test(pw) },
  { label: 'One special character', test: (pw) => /[^A-Za-z0-9]/.test(pw) },
];

export function ChangePassword(): JSX.Element {
  const navigate = useNavigate();
  const { user, mustChangePassword } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ruleResults = PASSWORD_RULES.map((rule) => ({ label: rule.label, met: rule.test(next) }));
  const allRulesMet = ruleResults.every((r) => r.met);
  const isDefault = next === DEFAULT_PASSWORD;
  const passwordsMatch = next.length > 0 && next === confirm;
  const canSubmit =
    current.length > 0 && allRulesMet && !isDefault && passwordsMatch && !submitting;

  useEffect(() => {
    const previous = document.title;
    document.title = mustChangePassword
      ? 'Set your password | Sunset Harbor Condominiums'
      : 'Change password | Sunset Harbor Condominiums';
    return () => {
      document.title = previous;
    };
  }, [mustChangePassword]);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);

    if (!canSubmit) return;

    setSubmitting(true);
    try {
      await changePassword(current, next);
      navigate('/portal', { replace: true });
    } catch (err) {
      console.error(err);
      setError(
        'We could not update your password. Make sure the current password is correct, then try again.',
      );
      setSubmitting(false);
    }
  }

  function onCancel(): void {
    signOut();
    navigate('/', { replace: true });
  }

  return (
    <>
      <PageHero
        eyebrow={mustChangePassword ? 'First-time setup' : 'Account'}
        title={mustChangePassword ? 'Set your password' : 'Change your password'}
        lead={
          mustChangePassword
            ? 'Three steps below. First, type the temporary password Welcome3873 in the Temporary password field. Then pick a new password of your own and retype it to confirm.'
            : 'Pick a new password.'
        }
      />

      <section className="section bg-brand-white">
        <Container>
          <div className="mx-auto max-w-md rounded-2xl border border-brand-surface bg-brand-white p-8 shadow-card md:p-10">
            <div className="mb-6 flex items-start gap-3">
              <span
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-yellow text-brand-charcoal"
                aria-hidden="true"
              >
                <KeyRound size={20} strokeWidth={1.75} />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-body-medium">
                  Signed in as
                </p>
                <p className="mt-0.5 text-sm font-semibold text-brand-charcoal">
                  {user?.email ?? '...'}
                  {ownerUnits(user).length > 0 ? ` · ${formatUnitLabel(ownerUnits(user))}` : ''}
                </p>
              </div>
            </div>

            {error ? (
              <div
                role="alert"
                className="mb-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
              >
                {error}
              </div>
            ) : null}

            {mustChangePassword ? (
              <div className="mb-6 rounded-md border-2 border-brand-yellow bg-brand-yellow/15 px-4 py-3 text-sm text-brand-charcoal">
                <p className="font-bold">Step 1: type the temporary password into the first field.</p>
                <p className="mt-1">
                  The field below is empty. Type{' '}
                  <span className="rounded bg-brand-yellow/40 px-1.5 py-0.5 font-mono font-semibold">
                    Welcome3873
                  </span>{' '}
                  into Temporary password, then choose a new password of your own.
                </p>
              </div>
            ) : null}

            <form onSubmit={onSubmit} noValidate>
              <label className="block">
                <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-brand-body-medium">
                  {mustChangePassword ? 'Temporary password' : 'Current password'}
                </span>
                <input
                  type="password"
                  name="current"
                  autoComplete="current-password"
                  required
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  disabled={submitting}
                  className="mt-2 block h-11 w-full rounded-md border border-brand-surface bg-brand-white px-3 text-base text-brand-charcoal focus:border-brand-yellow focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                />
              </label>

              <label className="mt-5 block">
                <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-brand-body-medium">
                  New password
                </span>
                <input
                  type="password"
                  name="next"
                  autoComplete="new-password"
                  required
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  disabled={submitting}
                  className="mt-2 block h-11 w-full rounded-md border border-brand-surface bg-brand-white px-3 text-base text-brand-charcoal focus:border-brand-yellow focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                />
                <div className="mt-3" aria-live="polite">
                  <ul className="space-y-1.5">
                    {ruleResults.map((rule) => (
                      <li key={rule.label} className="flex items-center gap-2 text-xs">
                        {rule.met ? (
                          <CheckCircle2
                            size={15}
                            className="shrink-0 text-green-600"
                            aria-hidden="true"
                          />
                        ) : (
                          <Circle
                            size={15}
                            className="shrink-0 text-brand-body-medium/50"
                            aria-hidden="true"
                          />
                        )}
                        <span className={rule.met ? 'text-green-700' : 'text-brand-body-medium'}>
                          {rule.label}
                          <span className="sr-only">{rule.met ? ' met' : ' not yet met'}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                  {isDefault ? (
                    <p className="mt-2 text-xs font-medium text-red-700">
                      Cannot be the shared default Welcome3873.
                    </p>
                  ) : null}
                </div>
              </label>

              <label className="mt-5 block">
                <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-brand-body-medium">
                  Confirm new password
                </span>
                <input
                  type="password"
                  name="confirm"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  disabled={submitting}
                  className="mt-2 block h-11 w-full rounded-md border border-brand-surface bg-brand-white px-3 text-base text-brand-charcoal focus:border-brand-yellow focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                />
                {confirm.length > 0 ? (
                  <span
                    className={`mt-2 flex items-center gap-2 text-xs font-medium ${
                      passwordsMatch ? 'text-green-700' : 'text-red-700'
                    }`}
                    aria-live="polite"
                  >
                    {passwordsMatch ? (
                      <CheckCircle2 size={14} className="shrink-0" aria-hidden="true" />
                    ) : (
                      <Circle size={14} className="shrink-0" aria-hidden="true" />
                    )}
                    {passwordsMatch ? 'Passwords match' : 'Passwords do not match yet'}
                  </span>
                ) : null}
              </label>

              <button
                type="submit"
                disabled={!canSubmit}
                className="mt-6 inline-flex h-12 w-full items-center justify-center gap-2 rounded-md bg-brand-yellow px-5 text-sm font-semibold text-brand-charcoal transition-colors hover:bg-brand-yellow-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-yellow focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                    Updating...
                  </>
                ) : (
                  <>
                    Save new password
                    <ArrowRight size={16} aria-hidden="true" />
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={onCancel}
                disabled={submitting}
                className="mt-4 block w-full text-center text-sm text-brand-body-medium underline decoration-brand-body-medium/40 underline-offset-4 hover:text-brand-charcoal hover:decoration-brand-charcoal"
              >
                Sign out and finish later
              </button>
            </form>
          </div>
        </Container>
      </section>
    </>
  );
}
